<template>
  <CRow>
    <CCol sm="12">
      <CCard class="mt-4 entry-form-card">
        <CCardHeader>
          <CIcon name="cil-description" class="mr-3"/>
          <h5>Protokoll</h5>
        </CCardHeader>

        <CCardBody class="entry-form-body">
          <ValidationObserver ref="formObserver" v-slot="{ invalid }">
            <CForm @submit.prevent="newEntry">
              <div class="entry-form-section entry-form-section--subtle">
                <div class="entry-form-section__title">Auftrag</div>
                <CRow class="mt-1">
                  <CCol sm="5">
                    <div ref="kundeField" :class="fieldAnchorClass('kunde')">
                      <Firma v-model="kunde"/>
                    </div>
                  </CCol>
                  <CCol sm="3">
                    <div ref="datumField" :class="fieldAnchorClass('datum')">
                      <Datum v-model="datum"/>
                    </div>
                    <Abgeschlossen v-model="abgeschlossen"/>
                  </CCol>
                  <CCol sm="4">
                    <div ref="leistungField" :class="fieldAnchorClass('leistung')">
                      <Leistung v-model="leistung"/>
                    </div>
                  </CCol>
                </CRow>

                <CRow>
                  <CCol sm="12">
                    <div ref="projektField" :class="fieldAnchorClass('projekt')">
                      <Projekt v-model="projekt" :kunde="kunde"/>
                    </div>
                  </CCol>
                </CRow>
              </div>

              <div class="entry-form-section">
                <div class="entry-form-section__title">Beschreibung</div>
                <div ref="arbeitsbeschreibungField" :class="fieldAnchorClass('arbeitsbeschreibung')">
                  <Arbeitsbeschreibung v-model="arbeitsbeschreibung"/>
                </div>
              </div>

              <div class="entry-form-section">
                <div ref="materialField" :class="fieldAnchorClass('material')">
                  <Material :key="materialFormKey" v-model="material" :material-vorgabe="materialVorgabe"/>
                </div>
              </div>

              <div class="entry-form-section">
                <div ref="arbeitszeitField" :class="fieldAnchorClass('arbeitszeit')">
                  <Arbeitszeit :key="arbeitszeitFormKey" v-model="arbeitszeit" :zukuenftig-berechnen="zukuenftigBerechnen"/>
                </div>
              </div>

              <div class="entry-form-section">
                <Tageskalender v-model="tageskalender" />
              </div>

              <div v-if="showSaveRetentionSelection" class="entry-form-section entry-form-section--retention">
                <SaveRetentionOptions
                  v-model="keepAfterSave"
                  :options="keepAfterSaveOptions"
                  :persist-value.sync="persistKeepAfterSave"
                />
              </div>

              <CRow class="mt-3">
                <CCol sm="12">
                  <FormValidationAssistant
                    :errors="validationErrors"
                    :visible="showValidationAssistant"
                    :show-summary="hasAttemptedSubmit"
                    @open="showValidationAssistant = true"
                    @close="showValidationAssistant = false"
                    @select="jumpToField"
                  />
                </CCol>
              </CRow>

              <CRow class="mt-3">
                <CCol sm="12">
                  <CSpinner color="" v-show="isSubmitting()"/>
                  <span class="fs-6" v-show="isSubmitting() && !successModal">wird gespeichert...</span>
                  <CButton
                    :color="btnColor(invalid)"
                    class="btn-protokoll-submit entry-form-submit btn-block font-weight-bold pt-3 pb-3"
                    :disabled="submitGoing"
                    type="submit"
                  >
                    <CIcon name="cil-library-add"/>
                    &nbsp;
                    Protokoll speichern
                  </CButton>
                </CCol>
              </CRow>

              <CModal
                title="Protokoll erfolgreich angelegt"
                color="success"
                size="sm"
                :show="successModal"
              >
                Das Protokoll wurde gespeichert! Wollen sie das Protokoll unterschreiben oder drucken?
                <br/>
                <br/>
                <div class="d-none alert alert-warning">
                  Die Protokolle werden automatisch jeden Tag um 23:00 Uhr als PDF gespeichert. <br/>
                  Die Protokolle werden erneut gespeichert, wenn diese aktualisiert wurden und z.B die Unterschrift nachgereicht wird.
                </div>
                <template #footer>
                  <div style="float: left">
                    <CButton color="default" @click="closeSite" class="mr-2">Schließen</CButton>
                  </div>
                  <div>
                    <CButton @click="print" color="info" class="mr-2">Unterschreiben oder Drucken</CButton>
                  </div>
                </template>
              </CModal>
            </CForm>
          </ValidationObserver>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import Firma from './components/Firma'
import Datum from './components/Datum'
import Abgeschlossen from './components/Abgeschlossen'
import Leistung from '@/components/LeistungsAuswahl'
import Projekt from './components/Projekt'
import Arbeitsbeschreibung from './components/Arbeitsbeschreibung'
import Material from './components/Material'
import Arbeitszeit from './components/Arbeitszeit'
import Tageskalender from './components/Tageskalender'
import SaveRetentionOptions from '@/components/SaveRetentionOptions'
import FormValidationAssistant from '@/components/FormValidationAssistant'
import Vue from 'vue'
import moment from 'moment/moment'

const KEEP_AFTER_SAVE_STORAGE_KEY = 'protokoll-keep-after-save'
const KEEP_AFTER_SAVE_PERSISTENCE_KEY = 'protokoll-keep-after-save-persist'
const SAVE_RETENTION_SELECTION_ENABLED = process.env.VUE_APP_ENABLE_SAVE_RETENTION_SELECTION !== 'false'

const createKeepAfterSaveDefaults = () => ({
  kunde: true,
  projekt: true,
  abgeschlossen: false,
  leistung: false,
  arbeitsbeschreibung: false,
  material: false,
  arbeitszeiten: false,
  tageskalender: false
})

const createMaterialVorgabeDefaults = () => ([
  {
    id: 1,
    stueckzahl: '1',
    artikelnr: '-',
    material: 'Klein-/Verdrahtungs-/u. Befestigungsmaterial'
  },
  {
    id: 2,
    stueckzahl: '1',
    artikelnr: '-',
    material: 'Anteilige Fahrzeugpauschale'
  }
])

export default {
  name: 'MitarbeiterEdit',
  components: {
    Firma,
    Datum,
    Abgeschlossen,
    Leistung,
    Projekt,
    Arbeitsbeschreibung,
    Material,
    Arbeitszeit,
    Tageskalender,
    SaveRetentionOptions,
    FormValidationAssistant
  },
  data () {
    return {
      submitting: false,
      submitGoing: false,
      kunde: '',
      datum: null,
      abgeschlossen: true,
      leistung: null,
      projekt: null,
      arbeitsbeschreibung: '',
      material: [],
      arbeitszeit: [],
      materialFormKey: 0,
      arbeitszeitFormKey: 0,
      successModal: false,
      successData: {},
      tageskalender: true,
      materialVorgabe: [],
      zukuenftigBerechnen: [],
      keepAfterSave: createKeepAfterSaveDefaults(),
      persistKeepAfterSave: false,
      highlightedField: null,
      showValidationAssistant: false,
      hasAttemptedSubmit: false,
      formError: null
    }
  },
  computed: {
    keepAfterSaveOptions () {
      return [
        { key: 'kunde', label: 'Kunde' },
        { key: 'projekt', label: 'Projekt' },
        { key: 'abgeschlossen', label: 'Abgeschlossen' },
        { key: 'leistung', label: 'Leistung' },
        { key: 'arbeitsbeschreibung', label: 'Arbeitsbeschreibung' },
        { key: 'material', label: 'Material' },
        { key: 'arbeitszeiten', label: 'Arbeitszeiten' },
        { key: 'tageskalender', label: 'Tageskalender' }
      ]
    },
    showSaveRetentionSelection () {
      return SAVE_RETENTION_SELECTION_ENABLED
    },
    validationErrors () {
      const errors = []
      const savableArbeitszeiten = this.getSavableArbeitszeiten()
      const savableMaterialEntries = this.getSavableMaterialEntries()

      if (!this.kunde || !this.kunde.kundenid) {
        errors.push({
          id: 'kunde',
          label: 'Kunde auswählen',
          anchor: 'kunde',
          focusStrategy: 'button'
        })
      }

      if (!this.datum) {
        errors.push({
          id: 'datum',
          label: 'Datum ergänzen',
          anchor: 'datum',
          focusStrategy: 'input'
        })
      }

      if (!this.leistung) {
        errors.push({
          id: 'leistung',
          label: 'Leistung auswählen',
          anchor: 'leistung',
          focusStrategy: 'input'
        })
      }

      if (!this.arbeitsbeschreibung || this.arbeitsbeschreibung.trim().length === 0) {
        errors.push({
          id: 'arbeitsbeschreibung',
          label: 'Arbeitsbeschreibung ausfüllen',
          anchor: 'arbeitsbeschreibung',
          focusStrategy: 'input'
        })
      }

      if (savableMaterialEntries.length === 0 && this.materialVorgabe.length === 0) {
        errors.push({
          id: 'material',
          label: 'Mindestens ein Material ergänzen',
          anchor: 'material',
          focusStrategy: 'interactive'
        })
      }

      if (savableArbeitszeiten.length === 0) {
        errors.push({
          id: 'arbeitszeit',
          label: 'Mindestens eine vollständige Arbeitszeit ergänzen',
          anchor: 'arbeitszeit',
          focusStrategy: 'input'
        })
      }

      return errors
    }
  },
  mounted () {
    this.loadKeepAfterSaveOptions()
    this.setDefaults()
  },
  watch: {
    kunde: function () {
      this.projekt = null
      this.loadArbeitszeitenAuto()
    },
    projekt: function () {
      this.loadArbeitszeitenAuto()
    },
    keepAfterSave: {
      deep: true,
      handler: function () {
        if (this.persistKeepAfterSave) {
          localStorage.setItem(KEEP_AFTER_SAVE_STORAGE_KEY, JSON.stringify(this.keepAfterSave))
        }
      }
    },
    persistKeepAfterSave: function () {
      if (this.persistKeepAfterSave) {
        localStorage.setItem(KEEP_AFTER_SAVE_PERSISTENCE_KEY, 'true')
        localStorage.setItem(KEEP_AFTER_SAVE_STORAGE_KEY, JSON.stringify(this.keepAfterSave))
      } else {
        localStorage.removeItem(KEEP_AFTER_SAVE_PERSISTENCE_KEY)
        localStorage.removeItem(KEEP_AFTER_SAVE_STORAGE_KEY)
        this.keepAfterSave = createKeepAfterSaveDefaults()
      }
    }
  },
  methods: {
    createMaterialVorgabeDefaultEntries () {
      return createMaterialVorgabeDefaults()
    },
    fieldAnchorClass (field) {
      return {
        'form-field-anchor': true,
        'form-field-anchor--active': this.highlightedField === field
      }
    },
    highlightField (field) {
      this.highlightedField = field

      window.clearTimeout(this.highlightTimeout)
      this.highlightTimeout = window.setTimeout(() => {
        if (this.highlightedField === field) {
          this.highlightedField = null
        }
      }, 1800)
    },
    findFocusableElement (container, focusStrategy = 'interactive') {
      if (!container) {
        return null
      }

      const selectors = {
        button: 'button:not([disabled])',
        input: 'input:not([disabled]), textarea:not([disabled]), select:not([disabled])',
        interactive: 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      }

      return container.querySelector(selectors[focusStrategy] || selectors.interactive)
    },
    jumpToField (error) {
      const container = this.$refs[`${error.anchor}Field`]
      this.showValidationAssistant = false

      if (!container) {
        return
      }

      container.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
      this.highlightField(error.anchor)

      window.setTimeout(() => {
        const focusable = this.findFocusableElement(container, error.focusStrategy)
        if (focusable && focusable.focus) {
          focusable.focus({ preventScroll: true })
        }
      }, 220)
    },
    async collectValidationErrors () {
      if (this.$refs.formObserver) {
        await this.$refs.formObserver.validate()
      }

      return this.validationErrors
    },
    loadKeepAfterSaveOptions () {
      const defaults = createKeepAfterSaveDefaults()

      if (!SAVE_RETENTION_SELECTION_ENABLED) {
        this.persistKeepAfterSave = false
        this.keepAfterSave = defaults
        return
      }

      this.persistKeepAfterSave = localStorage.getItem(KEEP_AFTER_SAVE_PERSISTENCE_KEY) === 'true'

      if (!this.persistKeepAfterSave) {
        this.keepAfterSave = defaults
        return
      }

      const rawValue = localStorage.getItem(KEEP_AFTER_SAVE_STORAGE_KEY)

      if (!rawValue) {
        this.keepAfterSave = defaults
        return
      }

      try {
        this.keepAfterSave = {
          ...defaults,
          ...JSON.parse(rawValue)
        }
      } catch (error) {
        this.keepAfterSave = defaults
      }
    },
    bumpMaterialFormKey () {
      this.materialFormKey += 1
    },
    bumpArbeitszeitFormKey () {
      this.arbeitszeitFormKey += 1
    },
    cloneValue (value) {
      return {
        ...value
      }
    },
    isMaterialSavable (material) {
      if (!material || !('stueckzahl' in material)) {
        return false
      }

      return String(material.stueckzahl).trim() !== ''
    },
    getSavableMaterialEntries () {
      return this.material.filter((material) => this.isMaterialSavable(material))
    },
    isArbeitszeitSavable (arbeitszeit) {
      if (!arbeitszeit) {
        return false
      }

      return 'datum' in arbeitszeit &&
        arbeitszeit.datum !== '' &&
        'dauer' in arbeitszeit &&
        arbeitszeit.dauer > 0 &&
        'mitarbeiter' in arbeitszeit &&
        arbeitszeit.mitarbeiter > 0
    },
    getSavableArbeitszeiten () {
      return this.arbeitszeit.filter((arbeitszeit) => this.isArbeitszeitSavable(arbeitszeit))
    },
    getManualArbeitszeiten () {
      return this.arbeitszeit
        .filter((eintrag) => !eintrag.auto)
        .map((eintrag) => this.cloneValue(eintrag))
    },
    setDefaults () {
      this.kunde = ''
      this.datum = new Date()
      this.abgeschlossen = true
      this.leistung = null
      this.projekt = null
      this.arbeitsbeschreibung = ''
      this.material = []
      this.arbeitszeit = []
      this.tageskalender = true
      this.materialVorgabe = this.createMaterialVorgabeDefaultEntries()
      this.zukuenftigBerechnen = []
      this.highlightedField = null
      this.showValidationAssistant = false
      this.hasAttemptedSubmit = false
      this.formError = null
      this.bumpMaterialFormKey()
      this.bumpArbeitszeitFormKey()
    },
    resetAfterSave () {
      const manualArbeitszeiten = this.keepAfterSave.arbeitszeiten ? this.getManualArbeitszeiten() : []

      this.submitGoing = false
      this.submitting = false
      this.datum = new Date()

      if (!this.keepAfterSave.kunde) {
        this.kunde = ''
      }

      if (!this.keepAfterSave.kunde || !this.keepAfterSave.projekt) {
        this.projekt = null
      }

      if (!this.keepAfterSave.abgeschlossen) {
        this.abgeschlossen = true
      }

      if (!this.keepAfterSave.leistung) {
        this.leistung = null
      }

      if (!this.keepAfterSave.arbeitsbeschreibung) {
        this.arbeitsbeschreibung = ''
      }

      if (!this.keepAfterSave.material) {
        this.material = []
        this.materialVorgabe = this.createMaterialVorgabeDefaultEntries()
        this.bumpMaterialFormKey()
      }

      if (this.keepAfterSave.arbeitszeiten) {
        this.arbeitszeit = manualArbeitszeiten
        this.zukuenftigBerechnen = []
      } else {
        this.arbeitszeit = []
        this.zukuenftigBerechnen = []
        this.bumpArbeitszeitFormKey()
      }

      if (!this.keepAfterSave.tageskalender) {
        this.tageskalender = true
      }

      this.highlightedField = null
      this.showValidationAssistant = false
      this.hasAttemptedSubmit = false

      if (this.kunde && this.keepAfterSave.kunde) {
        this.loadArbeitszeitenAuto()
      }
    },
    isSubmitting: function () {
      return this.submitting
    },
    canSubmit: function (observerInvalid = false) {
      return this.submitGoing || observerInvalid || this.validationErrors.length > 0
    },
    btnColor: function (observerInvalid = false) {
      if (!this.canSubmit(observerInvalid)) {
        return 'success'
      } else {
        return 'danger'
      }
    },
    async newEntry () {
      this.hasAttemptedSubmit = true
      this.formError = null

      const errors = await this.collectValidationErrors()
      if (errors.length > 0 || this.submitGoing) {
        if (errors.length > 0) {
          this.showValidationAssistant = true
        }
        return
      }

      const savableArbeitszeiten = this.getSavableArbeitszeiten()
      const savableMaterialEntries = this.getSavableMaterialEntries()

      this.submitGoing = true
      this.submitting = true

      try {
        const response = await Vue.axios.post('/protokoll/post', {
          datum: this.dateFormatter(this.datum),
          arbeitsbeschreibung: this.arbeitsbeschreibung,
          tageskalender: this.tageskalender,
          abgeschlossen: this.abgeschlossen,
          kunde: this.kunde.kundenid,
          projekt: this.projekt,
          leistung: this.leistung
        })

        if (!response.data.protokollid) {
          throw new Error('missing protokollid')
        }

        const protokollid = response.data.protokollid
        const materialPromises = [
          ...this.materialVorgabe.map((material) => {
            const normalizedMaterial = this.normalizeMaterialPayload(material)

            return Vue.axios.post('/material/post', {
              protokoll: protokollid,
              stueck: normalizedMaterial.stueckzahl,
              artikelnr: normalizedMaterial.artikelnr,
              material: normalizedMaterial.material
            })
          }),
          ...savableMaterialEntries.map((material) => {
            const normalizedMaterial = this.normalizeMaterialPayload(material)

            return Vue.axios.post('/material/post', {
              protokoll: protokollid,
              stueck: normalizedMaterial.stueckzahl,
              artikelnr: normalizedMaterial.artikelnr,
              material: normalizedMaterial.material
            })
          })
        ]

        const arbeitszeitPromises = savableArbeitszeiten.map(async (arbeitszeit) => {
          const arbeitszeitResponse = await Vue.axios.post('/arbeitszeit/post', {
            protokoll: protokollid,
            auto: arbeitszeit.auto || false,
            mitarbeiter: arbeitszeit.mitarbeiter,
            datum: this.dateFormatter(arbeitszeit.datum),
            dauer: arbeitszeit.dauer,
            taetigkeit: arbeitszeit.taetigkeit || ''
          })

          const followUpRequests = []

          if (arbeitszeit.auto) {
            followUpRequests.push(Vue.axios.delete(`/tageskalender/deleteauto/${arbeitszeitResponse.data.arbeitszeitid}/${arbeitszeit.tageskalenderid}`))
          } else {
            followUpRequests.push(
              this.saveImage(arbeitszeitResponse.data.arbeitszeitid, 'bild01', arbeitszeit.image01),
              this.saveImage(arbeitszeitResponse.data.arbeitszeitid, 'bild02', arbeitszeit.image02),
              this.saveImage(arbeitszeitResponse.data.arbeitszeitid, 'bild03', arbeitszeit.image03),
              this.saveImage(arbeitszeitResponse.data.arbeitszeitid, 'bild04', arbeitszeit.image04),
              this.saveImage(arbeitszeitResponse.data.arbeitszeitid, 'bild05', arbeitszeit.image05)
            )
          }

          await Promise.all(followUpRequests)
        })

        await Promise.all([
          ...materialPromises,
          ...arbeitszeitPromises
        ])

        this.resetAfterSave()
        this.$snotify.success('Protokoll wird gespeichert.', {
          position: 'rightTop',
          timeout: 4000
        })
        const routeData = this.$router.resolve({ path: `/print/${protokollid}` })
        this.successData = {
          link: routeData.href
        }
        this.successModal = true
      } catch (error) {
        if (error.response && error.response.data) {
          this.formError = error.response.data
        }

        this.$snotify.error('Unbestimmter Fehler beim Speichern eines Protokolls.', {
          position: 'rightTop',
          timeout: 4000
        })
        this.submitting = false
        this.submitGoing = false
      }
    },
    closeSite () {
      this.successModal = false
      this.submitGoing = false
    },
    print () {
      this.successModal = false
      window.open(this.successData.link, '_blank')
    },
    dateFormatter (value) {
      if (moment(value, 'DD.MM.YYYY', true).isValid()) {
        value = moment(value, 'DD.MM.YYYY', true).format('DD.MM.YYYY')
      }

      return value
    },
    normalizeMaterialPayload (material) {
      return {
        ...material,
        stueckzahl: typeof material.stueckzahl === 'string' ? material.stueckzahl.trim() : material.stueckzahl,
        artikelnr: typeof material.artikelnr === 'string' ? material.artikelnr.trim() : material.artikelnr,
        material: typeof material.material === 'string' ? material.material.trim() : material.material
      }
    },
    loadArbeitszeitenAuto () {
      if (!this.kunde || this.kunde === '') {
        this.zukuenftigBerechnen = []
        return
      }

      const result = []

      Vue.axios.post('/tageskalender/zukuenftigberechnen', {
        kundenid: this.kunde.kundenid,
        projektid: this.projekt
      }).then((response) => {
        if (response.data && response.data.length > 0) {
          this._.forEach(response.data, (tk) => {
            result.push({
              auto: true,
              tageskalenderid: tk.tageskalenderid,
              datum: this.dateFormatter(tk.datum),
              dauer: parseFloat(tk.dauer),
              mitarbeiter: tk.mitarbeiter.mitarbeiterid,
              taetigkeit: tk.taetigkeit,
              bilder: tk.bilder
            })
          })
        }

        this.zukuenftigBerechnen = result
      })
    },
    saveImage (arbeitszeitid, selektor, image) {
      if (image) {
        const formData = new FormData()
        formData.append('selektor', selektor)
        formData.append('dbid', arbeitszeitid)
        formData.append('target', 'arbeitszeit')
        formData.append('uploadfile', image)

        return Vue.axios.post(
          '/bilder/upload',
          formData,
          {
            headers: {
              'content-type': `multipart/form-data; boundary=${formData._boundary}`
            }
          }
        )
      }

      return Promise.resolve()
    }
  }
}
</script>

<style lang="scss">
button.btn-protokoll-submit:disabled {
  cursor: default;
}

.entry-form-card {
  border-radius: 8px;
  box-shadow: none;
  overflow: hidden;
}

.entry-form-card > .card-header {
  background: #fff;
  border-bottom: 1px solid #d8dbe0;
  padding: 0.85rem 1rem;
}

.entry-form-body {
  background: #fff;
  padding: 1rem 1rem 0.9rem;
}

.entry-form-section {
  background: #fff;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  margin-bottom: 0.9rem;
  padding: 0.9rem 1rem;
}

.entry-form-section--subtle {
  background: #f8f9fb;
}

.entry-form-section--retention {
  background: transparent;
  border: 0;
  padding: 0;
}

.entry-form-section__title {
  color: #1f2933;
  font-size: 0.92rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.entry-form-submit {
  border-radius: 6px;
  box-shadow: none;
}

.form-field-anchor {
  border-radius: 6px;
  scroll-margin-top: 5rem;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.form-field-anchor--active {
  background: #f3f8ff;
  box-shadow: 0 0 0 2px rgba(45, 114, 217, 0.24);
}

@media (max-width: 575.98px) {
  .entry-form-body {
    padding: 0.85rem 0.85rem 0.75rem;
  }

  .entry-form-section {
    border-radius: 8px;
    padding: 0.8rem 0.85rem;
    margin-bottom: 0.8rem;
  }
}
</style>
